
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";

interface User {
  id: string;
  username: string;
  avatar_url?: string;
  bio?: string | null;  // Changed from short_bio to bio
}

interface FollowersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  profileId: string;
  type: 'followers' | 'following';
}

export const FollowersDialog = ({ isOpen, onClose, title, profileId, type }: FollowersDialogProps) => {
  const navigate = useNavigate();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['user-follows', profileId, type],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_follows')
        .select(`
          ${type === 'followers' 
            ? 'follower:follower_id (id, username, avatar_url, bio)' 
            : 'following:following_id (id, username, avatar_url, bio)'
          }
        `)
        .eq(type === 'followers' ? 'following_id' : 'follower_id', profileId);

      if (error) throw error;

      return data.map(item => {
        const user = type === 'followers' ? 
          (item as any).follower : 
          (item as any).following;
        return user as User;
      });
    },
    enabled: isOpen && !!profileId,
  });

  const handleUserClick = (userId: string) => {
    navigate(`/profile/${userId}`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-flux-black text-white border border-flux-cyan/20 max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-flux-cyan">{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[50vh] pr-4">
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center text-gray-400">Loading...</div>
            ) : users.length === 0 ? (
              <div className="text-center text-gray-400">No {title.toLowerCase()} yet</div>
            ) : (
              users.map((user) => (
                <div 
                  key={user.id} 
                  className="flex gap-4 p-3 rounded-lg hover:bg-flux-cyan/10 transition-colors cursor-pointer"
                  onClick={() => handleUserClick(user.id)}
                >
                  <Avatar>
                    <AvatarImage src={user.avatar_url} />
                    <AvatarFallback>{user.username?.[0].toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white">{user.username}</div>
                    {user.bio && (
                      <p className="text-sm text-gray-400 line-clamp-2">{user.bio}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

